import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, User, LogOut, LayoutDashboard, ArrowRight } from 'lucide-react';
import { useState } from 'react';

const CRM_URL = process.env.REACT_APP_CRM_URL || 'https://crm.phillyrideshare.com';

const Brand = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
    <img src="/logo.png" alt="Fair Rideshare" className="w-10 h-10 rounded-xl" />
    <div className="leading-none">
      <span className="block font-display font-bold text-lg text-slate-900">Fair Rideshare</span>
      <span className="block text-[10px] font-semibold tracking-[0.18em] text-slate-400 mt-0.5">PHILADELPHIA · LLC</span>
    </div>
  </Link>
);

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { to: `${CRM_URL}/book`, label: 'Fleet', testid: 'nav-fleet', external: true },
    { to: '/#how', label: 'How it works', testid: 'nav-how' },
    { to: '/about', label: 'About', testid: 'nav-about' },
    { to: '/contact', label: 'Contact', testid: 'nav-contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Brand />

          <div className="hidden md:flex items-center gap-9">
            {navLinks.map((l) => (
              l.external ? (
                <a key={l.to} href={l.to} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-testid={l.testid}>
                  {l.label}
                </a>
              ) : (
                <Link key={l.to} to={l.to} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-testid={l.testid}>
                  {l.label}
                </Link>
              )
            ))}
          </div>

          <div className="flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2 rounded-lg" data-testid="user-menu-trigger">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate(user.role === 'admin' ? '/admin' : '/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <a href={`${CRM_URL}/login`} className="hidden sm:inline text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors" data-testid="nav-login">
                  Staff login
                </a>
                <a href={`${CRM_URL}/book`} className="hidden sm:inline-flex">
                  <button className="btn-dark text-sm" data-testid="nav-book-now">
                    Book now <ArrowRight className="w-4 h-4" />
                  </button>
                </a>
              </>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-1">
              {navLinks.map((l) => (
                l.external ? (
                  <a key={l.to} href={l.to} className="px-4 py-2.5 text-slate-700 font-medium hover:bg-slate-100 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.to} to={l.to} className="px-4 py-2.5 text-slate-700 font-medium hover:bg-slate-100 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                    {l.label}
                  </Link>
                )
              ))}
              <a href={`${CRM_URL}/book`} className="px-4 py-2.5 text-amber-600 font-semibold hover:bg-amber-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                Book now
              </a>
              {!user && (
                <a href={`${CRM_URL}/login`} className="px-4 py-2.5 text-slate-500 font-medium hover:bg-slate-100 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                  Staff login
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
